import { httpsCallable } from 'firebase/functions';
import { functions } from '../Firebase';

/**
 * Custom Plan Service
 *
 * Frontend service for managing custom/enterprise plans that are created
 * manually and are NOT managed by Lemon Squeezy.
 */

export interface CustomPlanLimits {
  domains?: number | string;
  funnels?: number | string;
  teamMembers?: number | string;
  engineeringHours?: number | string;
  customFeatures?: string[];
}

export interface CreateCustomPlanData {
  accountId: string;
  planType: 'custom';
  planName: string;
  billingInterval: 'month' | 'year';
  price: number; // Amount in cents
  currency?: string;
  customLimits?: CustomPlanLimits;
  startDate?: string; // ISO datetime string
  endDate?: string; // ISO datetime string
  contractId?: string;
  notes?: string;
}

export interface UpdateCustomPlanData {
  accountId: string;
  planName?: string;
  price?: number;
  currency?: string;
  endDate?: string;
  status?: 'active' | 'paused' | 'cancelled' | 'expired';
  customLimits?: CustomPlanLimits;
  notes?: string;
}

export interface CancelCustomPlanData {
  accountId: string;
  cancelImmediately?: boolean;
}

/**
 * Create a custom plan for an account
 * Admin/sales function only
 */
export async function createCustomPlan(data: CreateCustomPlanData) {
  const fn = httpsCallable(functions, 'subscriptions/createCustomPlan');
  const result = await fn(data);
  return result.data;
}

/**
 * Update an existing custom plan
 * Admin function only
 */
export async function updateCustomPlan(data: UpdateCustomPlanData) {
  const fn = httpsCallable(functions, 'subscriptions/updateCustomPlan');
  const result = await fn(data);
  return result.data;
}

/**
 * Cancel a custom plan
 * Admin function only
 */
export async function cancelCustomPlan(data: CancelCustomPlanData) {
  const fn = httpsCallable(functions, 'subscriptions/cancelCustomPlan');
  const result = await fn(data);
  return result.data;
}

export interface UpdateSubscriptionLimitsData {
  accountId: string;
  customLimits: {
    domains?: number | null;
    pages?: number | null;
    publishedPages?: number | null;
    members?: number | null;
    aiCredits?: number | null;
    buffer?: number;
  };
}

/**
 * Update subscription limits for any subscription (including LS custom products)
 * Admin function only - updates both subscription.customLimits and account.limits
 */
export async function updateSubscriptionLimits(
  data: UpdateSubscriptionLimitsData,
) {
  const fn = httpsCallable(functions, 'subscriptions/updateSubscriptionLimits');
  const result = await fn(data);
  return result.data;
}
